import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType, NavigationType } from "react-router-dom";

/*
 * Start a new page at the top (Design V2 closure polish).
 *
 * A React Router PUSH is a `history.pushState()` call, and `pushState` does not
 * touch the document's scroll position. The next page therefore rendered into
 * the same DOM position while the document kept whatever offset the previous
 * page had left behind -- scroll Home to the bottom, tap PROGRAM, and `/program`
 * opened mid-page. Nothing in the application reset it, so this was every route
 * transition, not one surface.
 *
 * WHAT OWNS THE SCROLL. Every route inside `MainLayout` scrolls the document:
 * `mainLayout.css` declares no `overflow`, and the page roots declare only
 * `overflow-x: clip`, which does not turn the other axis into a scroll port.
 * The `overflow-y: auto` elements in the tree (the builder search dock and its
 * results, the modal surface, the Settings drawer's own scroller) are widgets
 * inside a page, never the page scroller, so resetting the document is correct
 * for every route this hook sees and reaches none of them.
 *
 * WHY PATHNAME. Keying on `location.pathname` -- rather than `location.key` or
 * the location object -- is what keeps two live "the URL changed but the page
 * did not" paths from being treated as navigation:
 *
 *   - `ProgramDetails` normalises its `workoutId` with
 *     `setSearchParams(next, { replace: true })` on mount;
 *   - `HomeDashboard` strips the `?settings=…` deep-link query with
 *     `navigate(location.pathname, { replace: true })`.
 *
 * Both keep the same pathname, so neither re-runs this effect. Local-state
 * navigation that never touches the URL at all -- the Cycle Viewer's week/day
 * rails, the Settings drawer's screen stack -- is outside the router and so is
 * unaffected by construction.
 *
 * WHY NOT POP. Back and Forward keep the browser's own restoration behaviour.
 * Forcing the top on POP would actively destroy the position a user is
 * returning to. Deterministic Back restoration is a separate, deferred piece of
 * work; this hook deliberately does nothing on POP rather than guessing.
 *
 * Scoped to `MainLayout` on purpose: auth, onboarding and the bootstrap screen
 * render outside it and already own their own scroll behaviour.
 */
export default function useRouteScrollReset() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  // The pathname this hook last acted on. `null` means nothing has been
  // navigated yet, which is how the first render is told apart from a
  // navigation: on entry the browser has already placed the document, and a
  // reset there would fight a reload or a restored session.
  const previousPathnameRef = useRef(null);

  // Layout effect, not effect: this runs before the browser paints the new
  // route, so the page is never briefly visible at the old offset.
  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;

    if (previousPathname === null) {
      return;
    }

    // The comparison is against the pathname this hook last SAW, not against
    // the effect's dependencies. `navigationType` is a dependency because the
    // same pathname can be re-entered under a different type, but it must never
    // be the thing that triggers a reset: `ProgramDetails` re-runs its
    // `setSearchParams(…, { replace: true })` normalisation whenever the active
    // workout changes, which flips PUSH to REPLACE on an unchanged pathname. A
    // reset there would yank the page to the top while the user is reading it.
    if (previousPathname === pathname) {
      return;
    }

    if (navigationType === NavigationType.Pop) {
      return;
    }

    scrollDocumentToTop();
  }, [pathname, navigationType]);
}

// `window.scrollTo` is the real seam; `document.scrollingElement` is the
// fallback for environments that stub the window without it (jsdom gives us
// both, and a page under test must not throw here).
function scrollDocumentToTop() {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.scrollTo === "function") {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return;
  }

  const scrollingElement =
    typeof document !== "undefined" ? document.scrollingElement : null;

  if (scrollingElement) {
    scrollingElement.scrollTop = 0;
    scrollingElement.scrollLeft = 0;
  }
}
